<template>

  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>{{ $t('Entries') }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button
            v-if="$can('manage', 'All')"
            v-b-toggle.sidebar-create-organization
            variant="primary"
          >
            {{ $t('Create') }}
          </b-button>
          <div>
            <b-dropdown
              variant="success"
              toggle-class="text-decoration-none"
              no-caret
              class="ml-1"
            >
              <template #button-content>
                <b-icon icon="file-earmark-excel" /> Excel
              </template>
              <b-dropdown-item @click="download(STATUS_ALL)">
                {{ $t('All') }}
              </b-dropdown-item>
              <b-dropdown-item @click="download(STATUS_ADMIN_NOT_ATTACHED)">
                {{ $t('Admin not attached') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('Search...')"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="organizationTable"
      :items="list"
      responsive
      :fields="tableColumns"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      :empty-text="$t('No matching records found')"
      :sort-desc.sync="isSortDirDesc"
      :busy="isBusy"
      class="position-relative"
    >
      <template #head()="data">
        {{ $t(data.label) }}
      </template>

      <template #cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <!-- Column: Id -->
      <template #cell(name)="data">
        {{ $langName(data.value) }}
      </template>
      <!-- Column: admin -->
      <template #cell(org_admin)="data">
        <b-badge
          v-for="user in data.item.org_admin"
          v-show="data.item.org_admin"
          :key="user.id"
          variant="success"
        >
          <router-link
            :title="user.full_name"
            :to="{ name: 'user', params: { id: get_admin_name(user).id }}"
          >
            {{ get_admin_name(user).pin }}
          </router-link>
        </b-badge>

        <b-badge
          v-show="!data.item.org_admin"
          variant="danger"
        >
          {{ $t('Not set') }}

        </b-badge></template>
      <template #cell(region_name)="data">
        {{ $langName(data.value.name) }}
      </template>

      <template #cell(short_name)="data">
        {{ $langName(data.value) }}
      </template>

      <template #cell(parent)="data">
        {{ $langName(data.value) }}
      </template>
      <template #cell(type_org)="data">
        {{ $langName(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge :variant="getOrganizationStatusColor(data.value)">
          {{ $langName(getOrganizationStatus(data.value).name) }}
        </b-badge>
      </template>

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            variant="primary"
            label="Text Centered"
          />
        </div>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <feather-icon
            v-if="$can('manage', 'All')"
            :id="`invoice-row-${data.item.id}-edit-icon`"
            v-b-toggle.sidebar-create-organization
            icon="EditIcon"
            class="mr-1 cursor-pointer"
            size="16"
            @click="update(data.item)"
          />

          <feather-icon
            :id="`invoice-row-${data.item.id}-preview-icon`"
            icon="EyeIcon"
            size="16"
            class="mr-1 cursor-pointer"
            @click="$router.push({ name: 'organization', params: { id: data.item.id }})"
          />
        </div>
      </template>

    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">{{ $t("table summary", { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }} </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >

          <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>
      </b-row>
      <create @refetchData="refetchData" />
    </div>
  </b-card>

</template>

<script>
import {
  BCard, BBadge, BRow, BCol, BFormInput, BButton, BSpinner,
  BTable, BDropdown, BDropdownItem, BPagination, VBToggle,
  BIcon,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import config from './config'
import organizationModule from '@/store/organization/index'
import regionModule from '@/store/region/index'
import Create from './Create.vue'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BBadge,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    vSelect,
    Create,
    BSpinner,
    BIcon,
  },
  computed: {
    orgTypes: {
      get() {
        return this.$store.state.staticModule.orgTypes
      },
    },
  },
  created() {
    store.dispatch('staticModule/orgType')
    store.dispatch(`${this.MODULE_REGION}/list`, { limit: 9999 })
    this.$store.dispatch(`${this.MODULE_ORGANIZATION}/ministries`)
    this.$store.commit(`${this.MODULE_ORGANIZATION}/FRESH`)
  },
  methods: {
    get_admin_name(user) {
      if (!user) {
        return null
      }
      return user
    },
    remove(data) {
      this.$removeSwal(this, data, `${this.MODULE_ORGANIZATION}/del`, this.refetchData)
    },
    update(data) {
      this.$store.commit(`${this.MODULE_ORGANIZATION}/UPDATE`, data)
    },
    getOrgTypeName(id) {
      const orgType = this.$store.getters['staticModule/getOrgType'](id)
      return orgType ? orgType.value : ''
    },
    download(status) {
      this.$store.dispatch(`${this.MODULE_ORGANIZATION}/excel`, status).then(res => {
        const link = document.createElement('a')
        link.href = res.data.path
        link.setAttribute('download', true)
        document.body.appendChild(link)
        link.click()
      }).catch(err => {
        this.$errorSwal(this, err)
      })
    },
    getOrganizationStatus(id) {
      return this.$store.getters['staticModule/getOrganizationStatus'](id)
    },
    getOrganizationStatusColor(id) {
      return this.$store.getters['staticModule/getOrganizationStatusColor'](id)
    },
  },
  setup() {
    const STATUS_ALL = 200
    const STATUS_ADMIN_NOT_ATTACHED = 100
    const MODULE_ORGANIZATION = 'organization'
    const MODULE_REGION = 'region'
    // Register module
    if (!store.hasModule(MODULE_ORGANIZATION)) store.registerModule(MODULE_ORGANIZATION, organizationModule)
    if (!store.hasModule(MODULE_REGION)) store.registerModule(MODULE_REGION, regionModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MODULE_ORGANIZATION)) store.unregisterModule(MODULE_ORGANIZATION)
      if (store.hasModule(MODULE_REGION)) store.unregisterModule(MODULE_REGION)
    })

    return {
      MODULE_ORGANIZATION,
      MODULE_REGION,
      STATUS_ADMIN_NOT_ATTACHED,
      STATUS_ALL,
      ...config(),
    }
  },
}
</script>
